import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import CalendarEntryChip from "./CalendarEntryChip";
import { getDueDatePart, getDueTimePart } from "../shared/utils/dueDateTime";

const DEFAULT_CHIP_STYLE = { fontSize: "10px", padding: 0 };
const ITEM_WRAP_STYLE = {
  fontSize: "10px",
  padding: "4px",
  marginBottom: "2px",
  borderRadius: "4px",
  minWidth: 0,
};

function defaultGetItemKey(item, index) {
  const id = item?._id != null ? String(item._id) : "item";
  return `${id}-${index}`;
}

function defaultGetLabel(item, getDateValue) {
  const name = (item?.name || "Item").trim() || "Item";
  const dateValue = getDateValue(item);
  return getDueTimePart(dateValue)
    ? `${name} · ${dayjs(dateValue).format("h:mm A")}`
    : name;
}

function defaultGetSortValue(item, getDateValue) {
  const dateValue = getDateValue(item);
  const d = dayjs(dateValue);
  return d.isValid() ? d.valueOf() : 0;
}

function CalendarDayItems({
  items,
  dateStr,
  expandedDates,
  setExpandedDates,
  onItemClick,
  getDateValue,
  getItemKey,
  getLabel,
  getSortValue,
  getChipClassName,
  getItemBackgroundColor,
  moreButtonClassName,
}) {
  const dateItems = useMemo(() => {
    const filtered = (items || []).filter((item) => getDueDatePart(getDateValue(item)) === dateStr);
    return filtered.slice().sort((a, b) => {
      const av = getSortValue ? getSortValue(a) : defaultGetSortValue(a, getDateValue);
      const bv = getSortValue ? getSortValue(b) : defaultGetSortValue(b, getDateValue);
      return av - bv;
    });
  }, [items, dateStr, getDateValue, getSortValue]);

  const isExpanded = expandedDates.has(dateStr);
  const visibleItems = isExpanded ? dateItems : dateItems.slice(0, 3);

  return (
    <>
      {visibleItems.map((item, index) => {
        const label = getLabel
          ? getLabel(item)
          : defaultGetLabel(item, getDateValue);
        const clickHandler = onItemClick ? () => onItemClick(item) : undefined;
        const backgroundColor = getItemBackgroundColor
          ? getItemBackgroundColor(item)
          : "#e7f3ff";
        const chipClassName = getChipClassName ? getChipClassName(item) : "btn-link";
        return (
          <div
            key={getItemKey ? getItemKey(item, index) : defaultGetItemKey(item, index)}
            style={{ ...ITEM_WRAP_STYLE, backgroundColor }}
          >
            {clickHandler ? (
              <CalendarEntryChip
                title={label}
                onClick={clickHandler}
                className={chipClassName}
                style={DEFAULT_CHIP_STYLE}
              />
            ) : (
              <span
                title={label}
                style={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </span>
            )}
          </div>
        );
      })}
      {dateItems.length > 3 && (
        <button
          type="button"
          onClick={() =>
            setExpandedDates((previous) => {
              const next = new Set(previous);
              if (isExpanded) next.delete(dateStr);
              else next.add(dateStr);
              return next;
            })
          }
          className={moreButtonClassName || "btn-link"}
          title={isExpanded ? "Show fewer" : "Show all"}
        >
          {isExpanded ? "Show less" : `+${dateItems.length - 3} more`}
        </button>
      )}
    </>
  );
}

/**
 * Shared day/week/month schedule calendar used by Events, Event Calendar, Tasks, Surveys.
 */
export default function ScheduleCalendar({
  items,
  view,
  focusDateYmd,
  onItemClick,
  emptyDayMessage = "No items this day",
  getDateValue,
  getItemKey,
  getLabel,
  getSortValue,
  getChipClassName,
  getItemBackgroundColor,
  moreButtonClassName,
}) {
  const [expandedDates, setExpandedDates] = useState(() => new Set());
  const focus = dayjs(focusDateYmd);
  const resolveDateValue = getDateValue || ((item) => item?.start_datetime);

  const weekDays = useMemo(() => {
    const days = [];
    const focusDate = dayjs(focusDateYmd);
    const start = focusDate.startOf("week");
    const end = focusDate.endOf("week");
    for (let date = start; date.isBefore(end) || date.isSame(end, "day"); date = date.add(1, "day")) {
      days.push(date.format("YYYY-MM-DD"));
    }
    return days;
  }, [focusDateYmd]);

  const monthWeeks = useMemo(() => {
    const weeks = [];
    const focusDate = dayjs(focusDateYmd);
    let weekStart = focusDate.startOf("month").startOf("week");
    const calendarEnd = focusDate.endOf("month").endOf("week");
    while (weekStart.isBefore(calendarEnd) || weekStart.isSame(calendarEnd, "day")) {
      const week = [];
      for (let index = 0; index < 7; index += 1) {
        week.push(weekStart.add(index, "day").format("YYYY-MM-DD"));
      }
      weeks.push(week);
      weekStart = weekStart.add(7, "day");
    }
    return weeks;
  }, [focusDateYmd]);

  const itemsForFocusedDay = (items || []).filter(
    (item) => getDueDatePart(resolveDateValue(item)) === focusDateYmd
  );

  const dayItemsProps = {
    items,
    expandedDates,
    setExpandedDates,
    onItemClick,
    getDateValue: resolveDateValue,
    getItemKey,
    getLabel,
    getSortValue,
    getChipClassName,
    getItemBackgroundColor,
    moreButtonClassName,
  };

  return (
    <div
      style={{
        marginTop: "12px",
        border: "1px solid #dee2e6",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "#fff",
        width: "100%",
      }}
    >
      <div style={{ padding: "12px" }}>
        {view === "day" && (
          <div>
            <h3 style={{ margin: "0 0 10px 0", fontSize: "1rem" }}>
              {focus.format("dddd, MMMM D, YYYY")}
            </h3>
            {itemsForFocusedDay.length === 0 ? (
              <div style={{ padding: "12px", color: "#6c757d" }}>{emptyDayMessage}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <CalendarDayItems {...dayItemsProps} dateStr={focusDateYmd} />
              </div>
            )}
          </div>
        )}

        {view === "week" && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
              gap: "8px",
              minHeight: "320px",
            }}
          >
            {weekDays.map((dateStr) => (
              <div
                key={dateStr}
                style={{
                  border: "1px solid #e9ecef",
                  borderRadius: "6px",
                  padding: "6px",
                  minHeight: "200px",
                  minWidth: 0,
                  backgroundColor: dayjs(dateStr).isSame(dayjs(), "day") ? "#e7f3ff" : "#fff",
                }}
              >
                <div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "4px" }}>
                  {dayjs(dateStr).format("ddd D")}
                </div>
                <CalendarDayItems {...dayItemsProps} dateStr={dateStr} />
              </div>
            ))}
          </div>
        )}

        {view === "month" && (
          <div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                gap: "2px",
                marginBottom: "6px",
                textAlign: "center",
                fontSize: "12px",
                fontWeight: 600,
                color: "#6c757d",
              }}
            >
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div key={day}>{day}</div>
              ))}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {monthWeeks.map((week, weekIndex) => (
                <div
                  key={weekIndex}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                    gap: "4px",
                    minHeight: "100px",
                  }}
                >
                  {week.map((dateStr) => {
                    const inMonth = dayjs(dateStr).month() === focus.month();
                    return (
                      <div
                        key={dateStr}
                        style={{
                          border: "1px solid #e9ecef",
                          borderRadius: "6px",
                          padding: "6px",
                          minWidth: 0,
                          backgroundColor: !inMonth
                            ? "#f8f9fa"
                            : dayjs(dateStr).isSame(dayjs(), "day")
                              ? "#e7f3ff"
                              : "#fff",
                          color: !inMonth ? "#adb5bd" : undefined,
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "4px" }}>
                          {dayjs(dateStr).format("D")}
                        </div>
                        <CalendarDayItems {...dayItemsProps} dateStr={dateStr} />
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
